/* « Revenez jouer » — EM-12 (D-69 ②, batch 14).
   The one message that invites a customer back: it fires the day her
   participation window reopens, not on a calendar of ours. That is why this
   screen reads the window from the seam (`@/lib/scope`) rather than carrying
   a number of its own — the merchant changes the window in his establishment
   and this date follows.

   Two things this screen exists to prevent:
   · a double send. If a Parcours is running, its own reminder already says
     « votre prochaine étape est disponible » at the same moment. That one
     wins; this one stands down.
   · an endless nudge. « Revenez jouer » stops after a few unanswered sends —
     a customer who does not come back is not a customer to keep writing to.

   The body is NOT written here: it comes from '@/emails' (read-only, the
   Orchestrator's), and renders through the same shell the inbox receives.
   Until that template lands, this screen says so instead of inventing copy. */
import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Card, KV, Note, Pill, Segmented, Switch } from '@/ui'
import { TEMPLATES } from '@/emails/templates'
import { renderEmail } from '@/emails/render'
import { useLocations } from '@/lib/api'
import { ALL, useScope, useWindowDays, windowSentence } from '@/lib/scope'
import { Retour } from './parts'

/** ⟦confirmable⟧ the default cap — two unanswered sends, then silence. The
 *  decision fixes « frequency-capped » without a number; this is the screen's
 *  proposal, and it is the merchant's to change. */
const PLAFOND_DEFAUT = '2'

export default function RevenezJouer() {
  const nav = useNavigate()
  const [scope] = useScope()
  const { data: locations } = useLocations()
  const [actif, setActif] = useState(true)          // D-69 ② : on by default
  const [plafond, setPlafond] = useState(PLAFOND_DEFAUT)

  const lieux = (locations ?? []).filter(l => l.status !== 'inactive')
  const lieu = (scope !== ALL ? lieux.find(l => l.id === scope) : undefined) ?? lieux[0]
  const jours = useWindowDays(lieu?.id ?? 'l0')

  const modele = TEMPLATES.find(t => t.id === 'revenez-jouer')
  const html = useMemo(() => (modele ? renderEmail(modele) : ''), [modele])
  const frame = useRef<HTMLIFrameElement>(null)
  const [hauteur, setHauteur] = useState(520)
  const ajuster = () => {
    const doc = frame.current?.contentDocument
    if (doc) setHauteur(Math.max(360, doc.documentElement.scrollHeight + 2))
  }

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 760 }}>
      <Retour />
      <div>
        <p className="t-eyebrow" style={{ margin: '0 0 4px' }}>Classe Retour</p>
        <h2 className="t-page" style={{ margin: 0 }}>« Revenez jouer »</h2>
      </div>
      <p className="f-help" style={{ margin: 0 }}>
        Le seul message qui invite une cliente à revenir jouer. Il part le jour où
        <b> sa</b> fenêtre se rouvre — pas le vôtre, pas le nôtre.
      </p>

      <KV rows={[
        ['Quand il part', <span key="q">
          <b>J+{jours + 1}</b> après sa dernière participation — le lendemain de la réouverture
        </span>],
        ['La fenêtre de {lieu}'.replace('{lieu}', lieu?.name ?? 'votre établissement'),
          <span key="f">{windowSentence(jours)} ·{' '}
            <a href="#" onClick={e => { e.preventDefault(); nav(`/etablissement/${lieu?.id ?? 'l0'}?vue=reglages`) }}>modifier</a>
            {' '}· <a href="#" onClick={e => { e.preventDefault(); nav('/campagnes/parcours/rythme') }}>ce qu’elle coûte</a>
          </span>],
        ['Envoi', <span key="e" style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          {actif ? 'activé' : 'désactivé'}
          <Switch checked={actif} onChange={e => setActif(e.currentTarget.checked)}
            aria-label="Envoyer « Revenez jouer »" />
          <span className="f-help">activé par défaut</span>
        </span>],
        ['On s’arrête après', <span key="p" style={{ display: 'inline-flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Segmented value={plafond} onChange={setPlafond}
            options={[['1', '1 relance'], ['2', '2 relances'], ['3', '3 relances'], ['0', 'jamais']]} />
          <span className="f-help">
            {plafond === '0'
              ? 'elle recevra l’invitation à chaque réouverture, sans fin.'
              : `sans retour après ${plafond} envoi${plafond === '1' ? '' : 's'}, on cesse d’écrire à cette cliente.`}
          </span>
        </span>],
        ['Si un parcours est en cours', <span key="j">
          c’est le rappel du parcours qui part <Pill tone="ink">celui-ci ne double jamais</Pill>
        </span>],
      ]} />

      <Note tone="ok">
        Ce message décrit un avantage qui existe déjà&nbsp;: sa fenêtre est rouverte, elle peut
        rejouer. C’est ce qui le classe en <b>Retour</b> — une préférence, désactivable seule,
        jamais couplée au marketing.
      </Note>

      <Note tone="candour">
        Il ne parle jamais d’avis&nbsp;: ni pour en demander un, ni pour en remercier.
        Revenir jouer ne dépend pas de ce qu’elle a écrit, et le message ne doit pas laisser
        croire le contraire.
      </Note>

      {modele ? (
        <>
          <p className="t-eyebrow" style={{ margin: '4px 0 0' }}>Boîte mail · aperçu</p>
          <iframe ref={frame} title="Aperçu — Revenez jouer" srcDoc={html} onLoad={ajuster}
            style={{ width: '100%', maxWidth: 620, height: hauteur, border: '1px solid var(--bmr-border)',
              borderRadius: 12, background: 'var(--bmr-surface-sunken)' }} />
          <p className="f-help" style={{ margin: 0 }}>De&nbsp;: {modele.from}</p>
          <p className="f-help" style={{ margin: 0 }}>{modele.sendRule}</p>
        </>
      ) : (
        <Card>
          <p className="t-card" style={{ margin: 0 }}>Le texte du message</p>
          <p className="f-help" style={{ margin: '6px 0 0' }}>
            Il est écrit une seule fois, dans la bibliothèque partagée, et s’affichera ici dès
            qu’il y sera — comme les autres messages. Ce que vous réglez sur cette page, le
            moment et la fréquence, ne change pas avec le texte.
          </p>
          <div className="actions">
            <Button size="sm" variant="quiet" onClick={() => nav('/messages/modeles')}>Voir la bibliothèque</Button>
            <Button size="sm" variant="quiet" onClick={() => nav('/messages/classes')}>Les classes de messages</Button>
          </div>
        </Card>
      )}
    </div>
  )
}
